import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";

import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "../../common/errorsDeclarations";
import * as comuniApi from "../../../api/anagrafiche/comuniApi";

export interface Nazione {
    id: number;
    codice_iso_nazione: string;
    nome: string;
    errorsStack?: ErrorsStack;
}

export interface Nazioni {
    count: number;
    page: number;
    num_pages: number;
    next?: URL;
    previous?: URL;
    ultimo_aggiornamento: string;
    results: Nazione[];
    errorsStack: ErrorsStack;
}

export interface Regione {
    codice_regione: number;
    name: string;
    errorsStack?: ErrorsStack;
}

export interface Regioni {
    count: number;
    page: number;
    num_pages: number;
    next?: URL;
    previous?: URL;
    ultimo_aggiornamento: string;
    results: Regione[];
    errorsStack: ErrorsStack;
}

export interface Comune {
    id: number;
    codice_istat: number;
    codice_catastale: string;
    nome: string;
    provincia: string;
    provincia_sigla: string;
    regione?: number;
    errorsStack?: ErrorsStack;
}

export interface Comuni {
    count: number;
    page: number;
    num_pages: number;
    next?: URL;
    previous?: URL;
    ultimo_aggiornamento: string;
    results: Comune[];
    errorsStack: ErrorsStack;
}

export interface ComuniStrutturaState {
    comuni?: Comuni;
    regioni?: Regioni;
    nazioni?: Nazioni;
}

const initialState: ComuniStrutturaState = {
    comuni: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        ultimo_aggiornamento: "",
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    regioni: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        ultimo_aggiornamento: "",
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    nazioni: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        ultimo_aggiornamento: "",
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
};

export const fetchComuni = createAsyncThunk(
    "comuni/fetchComuni",
    async (
        parametri: { ultimoAggiornamento?: string; nextUrl?: string },
        thunkApi
    ) => {
        return await comuniApi
            .fetchComuni(parametri?.ultimoAggiornamento, parametri?.nextUrl)
            .then((response) => {
                if (response.next) {
                    thunkApi.dispatch(fetchComuni({nextUrl: response.next}));
                }
                return response;
            });
    }
);

export const fetchRegioni = createAsyncThunk(
    "comuni/fetchRegioni",
    async (parametri: { nextUrl?: string }, thunkApi) => {
        return await comuniApi.fetchRegioni(parametri?.nextUrl).then((response) => {
            if (response.next) {
                thunkApi.dispatch(fetchRegioni({nextUrl: response.next}));
            }
            return response;
        });
    }
);

export const fetchNazioni = createAsyncThunk(
    "comuni/fetchNazioni",
    async (parametri: { nextUrl?: string }, thunkApi) => {
        return await comuniApi.fetchNazioni(parametri?.nextUrl).then((response) => {
            if (response.next) {
                thunkApi.dispatch(fetchNazioni({nextUrl: response.next}));
            }
            return response;
        });
    }
);

export const comuniSlice = createSlice({
    name: "comuni",
    initialState,
    reducers: {
        resetNazioni: (state) => {
            state.nazioni = {
                count: 0,
                page: 0,
                num_pages: 0,
                next: undefined,
                previous: undefined,
                ultimo_aggiornamento: "",
                results: [],
                errorsStack: {status: ErrorStatusTypes.OK},
            };
        },
    },
    extraReducers: (builder) => {
        // fetch Regimi Commerciali
        builder.addCase(fetchComuni.pending, (state, action) => {
            state.comuni ? (state.comuni.errorsStack = {status: ErrorStatusTypes.PENDING}) :
                (state.comuni = {
                    count: 0,
                    page: 0,
                    num_pages: 0,
                    next: undefined,
                    previous: undefined,
                    ultimo_aggiornamento: "",
                    results: [],
                    errorsStack: {status: ErrorStatusTypes.PENDING}
                })
        });
        builder.addCase(
            fetchComuni.fulfilled,
            (state, action: PayloadAction<Comuni>) => {
                state.comuni && (state.comuni.ultimo_aggiornamento = action.payload.ultimo_aggiornamento)
                // faccio il merge tra i record che ci sono in store e quelli che arrivano dalla fetch
                // paragorando per id e dando priorità a questi ultimi e poi riordino per nome

                const comuniMap = new Map<number, Comune>();

                // Aggiungi prima i comuni esistenti alla mappa
                state.comuni && (state.comuni.results.forEach((comune) => comuniMap.set(comune.id, comune)))

                // Aggiorna la mappa con i comuni dal payload, sovrascrivendo i duplicati
                action.payload.results.forEach((comune) => {
                    comuniMap.set(comune.id, comune); // Sovrascrive se l'id è uguale
                });

                // Converti la mappa in un array
                state.comuni && (state.comuni.results = Array.from(comuniMap.values()))

                // e poi li riordino per nome comune
                state.comuni && (state.comuni.results = state.comuni.results.sort((a, b) => a.nome.localeCompare(b.nome)))
                state.comuni && (state.comuni.errorsStack = {status: ErrorStatusTypes.OK})
            }
        );
        builder.addCase(fetchComuni.rejected, (state, action) => {
            // state.comuni && (state.comuni.errorsStack = parseErrorMessage(action.error))
        });

        // fetch Regioni
        builder.addCase(fetchRegioni.pending, (state, action) => {
            state.regioni ? (state.regioni.errorsStack = {status: ErrorStatusTypes.PENDING}) :
                (state.regioni = {
                    count: 0,
                    page: 0,
                    num_pages: 0,
                    next: undefined,
                    previous: undefined,
                    ultimo_aggiornamento: "",
                    results: [],
                    errorsStack: {status: ErrorStatusTypes.PENDING}
                })
        });
        builder.addCase(
            fetchRegioni.fulfilled,
            (state, action: PayloadAction<Regioni>) => {
                state.regioni && (state.regioni.ultimo_aggiornamento = action.payload.ultimo_aggiornamento)
                // faccio il merge tra i record che ci sono in store e quelli che arrivano dalla fetch
                // paragorando per id e dando priorità a questi ultimi e poi riordino per nome

                const regioniMap = new Map<number, Regione>();

                // Aggiungi prima le nazioni esistenti alla mappa
                state.regioni && (state.regioni.results.forEach((regione) => {
                    regioniMap.set(regione.codice_regione, regione);
                }))

                // Aggiorna la mappa con le nazioni dal payload, sovrascrivendo i duplicati
                action.payload.results.forEach((regione) => {
                    regioniMap.set(regione.codice_regione, regione); // Sovrascrive se l'id è uguale
                });

                // Converti la mappa in un array
                state.regioni && (state.regioni.results = Array.from(regioniMap.values()))

                // e poi li riordino per nome nazione
                state.regioni && (state.regioni.results = state.regioni.results.sort((a, b) => a.name.localeCompare(b.name)))
                state.regioni && (state.regioni.errorsStack = {status: ErrorStatusTypes.OK})
            }
        );
        builder.addCase(fetchRegioni.rejected, (state, action) => {
            state.regioni && (state.regioni.errorsStack = parseErrorMessage(action.error))
        });

        // fetch Nazioni
        builder.addCase(fetchNazioni.pending, (state, action) => {
            state.nazioni ? (state.nazioni.errorsStack = {status: ErrorStatusTypes.PENDING}) :
                (
                    state.nazioni = {
                        count: 0,
                        page: 0,
                        num_pages: 0,
                        next: undefined,
                        previous: undefined,
                        ultimo_aggiornamento: "",
                        results: [],
                        errorsStack: {status: ErrorStatusTypes.PENDING}
                    }

                )
        });
        builder.addCase(
            fetchNazioni.fulfilled,
            (state, action: PayloadAction<Nazioni>) => {
                state.nazioni && (state.nazioni.ultimo_aggiornamento = action.payload.ultimo_aggiornamento)
                // faccio il merge tra i record che ci sono in store e quelli che arrivano dalla fetch
                // paragorando per id e dando priorità a questi ultimi e poi riordino per nome

                const nazioniMap = new Map<number, Nazione>();

                // Aggiungi prima le nazioni esistenti alla mappa
                state.nazioni && (state.nazioni.results.forEach((nazione) => nazioniMap.set(nazione.id, nazione)))

                // Aggiorna la mappa con le nazioni dal payload, sovrascrivendo i duplicati
                action.payload.results.forEach((nazione) => {
                    nazioniMap.set(nazione.id, nazione); // Sovrascrive se l'id è uguale
                });

                // Converti la mappa in un array
                state.nazioni && (state.nazioni.results = Array.from(nazioniMap.values()))

                // e poi li riordino per nome nazione
                state.nazioni && (state.nazioni.results = state.nazioni.results.sort((a, b) => a.nome.localeCompare(b.nome)))
                state.nazioni && (state.nazioni.errorsStack = {status: ErrorStatusTypes.OK})
            }
        );
        builder.addCase(fetchNazioni.rejected, (state, action) => {
            state.nazioni && (state.nazioni.errorsStack = parseErrorMessage(action.error))
        });
    },
});

// Action creators are generated for each case reducer function
export const {resetNazioni} = comuniSlice.actions;

export const comuniSliceReducer = comuniSlice.reducer;
